import { useMemo } from 'react';
import { Chip } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { BusinessRounded as BusinessIcon, EcoRounded as EcoIcon, RoomRounded as RoomIcon } from '@material-ui/icons';
import { SEGMENT_CATEGORY, SegmentCategory } from '../../types/segment-category.type';
import { SEGMENT_CATEGORY_LABELS } from '../../constants/segment-category.constants';
import { RELAY_CHIP_BACKGROUND, getRelayChipTextColor } from '../../../shared/components/Chips/relay-chip.styles';

type SegmentCategoryChipProps = {
  category: SegmentCategory;
  size?: 'small' | 'medium';
};

const useStyles = makeStyles(() => ({
  root: (props: { backgroundColor: string; color: string }) => ({
    borderRadius: 4,
    textTransform: 'capitalize',
    backgroundColor: props.backgroundColor,
    color: props.color,

    '& .MuiChip-label': {
      display: 'flex',
      alignItems: 'center',
      gap: 6,

      '& > svg': {
        width: 16,
        height: 16,
        fill: props.color,
      },

      '& > span': {
        fontSize: 12,
        lineHeight: '14px',
      },
    },
  }),
}));

const getChipStyle = (
  category: SegmentCategory,
  relayTextColor: string
): { backgroundColor: string; color: string; icon: React.ReactElement } => {
  switch (category) {
    case SEGMENT_CATEGORY.ENTREPRISE:
      return { backgroundColor: '#2196f3', color: '#ffffff', icon: <BusinessIcon /> };
    case SEGMENT_CATEGORY.ECO:
      return { backgroundColor: '#4caf50', color: '#ffffff', icon: <EcoIcon /> };
    case SEGMENT_CATEGORY.RELAY:
      return { backgroundColor: RELAY_CHIP_BACKGROUND, color: relayTextColor, icon: <RoomIcon /> };
  }
};

export default function SegmentCategoryChip({ category, size = 'small' }: SegmentCategoryChipProps) {
  const relayTextColor = getRelayChipTextColor(useTheme());
  const { label, backgroundColor, color, icon } = useMemo(() => {
    return {
      label: SEGMENT_CATEGORY_LABELS[category],
      ...getChipStyle(category, relayTextColor),
    };
  }, [category, relayTextColor]);

  const classes = useStyles({ backgroundColor, color });

  return (
    <Chip
      className={classes.root}
      size={size}
      label={
        <>
          {icon}
          <span>{label}</span>
        </>
      }
    />
  );
}
